import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Field, Modal, Text } from '../../../../components';
import { OPERATOR_NAME_MAX, SIGNATORY_NAME_MAX } from '../../api/contract';
import { contractErrorText, contractNoticeTone, fieldErrorText, fieldErrorsOf } from '../../errors';
import { canContinue, validateNames, type NameErrors, type NameValues } from '../../form';
import type { Prefill } from '../../prefill';
import type { Dialog, Notice } from '../../state';
import * as S from './ContractDialog.styles';

export type ContractDialogProps = {
  /** closed | form (Start, or Start over). */
  dialog: Dialog;
  /** The form's initial values; re-seeded whenever the form opens. */
  prefill: Prefill;
  /** A Start or Start over is on its way. */
  sending: boolean;
  /** The refusal to show inside the dialog. */
  notice: Notice | null;
  onCancel: () => void;
  onContinue: (values: NameValues) => void;
};

type OpenDialog = Exclude<Dialog, { kind: 'closed' }>;
type Touched = { operator?: boolean; signatory?: boolean };

const valuesOf = (prefill: Prefill): NameValues => ({ operator: prefill.operator, signatory: prefill.signatory });

/**
 * Settings > Secure your brand's one popup, on the shared Modal: the names form (Start, and Start
 * over with its warning line). The operator name, and the signatory's when the prefill shows it, each
 * with its helper and its error. A send that lands closes it: the section's step cards turn into
 * Document sent, which is the confirmation.
 *
 * The names are seeded from `prefill` each time the form opens, never while it is open. A field's
 * own error shows once it has been edited, except "too long", which only a prefill can bring (the
 * field caps typing) and which shows at once; otherwise the field shows the error the service named
 * for it. Any other refusal is a notice under the fields. While a send is on its way everything is
 * disabled; Escape, the backdrop and the close button go to `onCancel`, which the section ignores
 * while sending. Continue hands the names on trimmed.
 *
 * While the Modal animates out, it keeps showing the form that was open (Start or Start over). `data-phase` on the body
 * is the live phase (`closed` from the moment a close goes through).
 */
export const ContractDialog = ({
  dialog,
  prefill,
  sending,
  notice,
  onCancel,
  onContinue,
}: ContractDialogProps): React.ReactElement => {
  const { t } = useTranslation();
  const [values, setValues] = React.useState<NameValues>(() => valuesOf(prefill));
  const [touched, setTouched] = React.useState<Touched>({});
  const [lastOpen, setLastOpen] = React.useState<OpenDialog | null>(dialog.kind === 'closed' ? null : dialog);
  // Adjusted while rendering (no effect needed, as in RotatePasswordDialog): the form is re-seeded
  // from the prefill each time it opens, and never while it is open.
  const [kind, setKind] = React.useState<Dialog['kind']>(dialog.kind);
  if (dialog.kind !== kind) {
    setKind(dialog.kind);
    if (dialog.kind === 'form') {
      setValues(valuesOf(prefill));
      setTouched({});
    }
  }
  if (dialog.kind !== 'closed' && dialog !== lastOpen) setLastOpen(dialog);
  // What the Modal shows: the open phase, or the last one while it animates out.
  const phase = dialog.kind === 'closed' ? lastOpen : dialog;

  const errors = validateNames(values, prefill.signatoryShown);
  const serviceFieldErrors = fieldErrorsOf(notice?.error ?? null);
  const continueOk = canContinue(errors) && !sending;
  const noticeText = notice ? contractErrorText(notice, t) : null;

  /** A field's own error once edited ("too long" at once), else the one the service named for it. */
  const fieldError = (field: keyof NameErrors): string | null => {
    const own = errors[field];
    const code = own !== undefined && (touched[field] === true || own === 'too_long') ? own : serviceFieldErrors[field];
    return code === undefined ? null : fieldErrorText(field, code, t);
  };
  const operatorError = fieldError('operator');
  const signatoryError = prefill.signatoryShown ? fieldError('signatory') : null;

  const edit =
    (field: keyof NameValues) =>
    (value: string): void => {
      setValues((current) => ({ ...current, [field]: value }));
      setTouched((current) => ({ ...current, [field]: true }));
    };

  // The gate lives in the handler as well as on the button.
  const onContinueClick = (): void => {
    if (!continueOk) return;
    onContinue({ operator: values.operator.trim(), signatory: values.signatory.trim() });
  };

  const footer = (
    <>
      <Button variant="outline" label={t('Cancel')} disabled={sending} onClick={onCancel} />
      <Button label={sending ? t('Please wait…') : t('Continue')} disabled={!continueOk} onClick={onContinueClick} />
    </>
  );

  return (
    <Modal open={dialog.kind !== 'closed'} title={t('Secure your brand')} onClose={onCancel} footer={footer}>
      <S.Fields data-testid="contract-dialog-body" data-phase={dialog.kind}>
        {phase?.kind === 'form' && phase.mode === 'restart' ? (
          <S.Line text="Your current contract will be cancelled and a new one sent." tag="paragraph" />
        ) : null}

        <S.FieldGroup>
          <Field
            label={t('Operator name')}
            value={values.operator}
            maxLength={OPERATOR_NAME_MAX}
            disabled={sending}
            onChange={edit('operator')}
          />
          <S.Helper text="This name identifies your party in the contract." />
          {operatorError !== null ? (
            <S.FieldError role="alert" data-testid="contract-dialog-error-operator">
              <Text raw text={operatorError} />
            </S.FieldError>
          ) : null}
        </S.FieldGroup>

        {prefill.signatoryShown ? (
          <S.FieldGroup>
            <Field
              label={t('Name of signatory')}
              value={values.signatory}
              maxLength={SIGNATORY_NAME_MAX}
              disabled={sending}
              onChange={edit('signatory')}
            />
            <S.Helper text="The person signing for the company." />
            {signatoryError !== null ? (
              <S.FieldError role="alert" data-testid="contract-dialog-error-signatory">
                <Text raw text={signatoryError} />
              </S.FieldError>
            ) : null}
          </S.FieldGroup>
        ) : null}

        {noticeText !== null ? (
          <div data-testid="contract-dialog-notice">
            <Callout tone={contractNoticeTone(notice?.error ?? null)} text={noticeText} />
          </div>
        ) : null}
      </S.Fields>
    </Modal>
  );
};

export default ContractDialog;
